import { getIconByKeyword } from "@/components/AccommodationModalDetailed/getIconByKeyword.ts";
import { ExtraCardProps } from "@/components/ExtraCard/ExtraCardProps.ts";
import NumInput from "@/components/Inputs/NumInput/NumInput.tsx";
import { useGetExtrasTranslated } from "@/components/ReservationOptions/hooks/useGetExtrasTranslated.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { translateCalculationRule } from "@/helpers/translation/translateCalculationRule.ts";
import { InputType, MandatoryExtra } from "@/helpers/types/types.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const ExtraCard = ({ extra, cartItem, isMandatory }: ExtraCardProps) => {
  const getExtraTranslated = useGetExtrasTranslated();
  const updateCartItemOptions = useEngineStore((state) => state.updateCartItemOptions);

  const currValue = cartItem.optionalExtras.find((opt) => opt.id === extra.id)?.value ?? 0;
  const displayValue = isMandatory ? (extra as MandatoryExtra).mandatoryQuantity ?? 0 : currValue;
  const isCheckbox = extra.inputType !== InputType.NumberSelect;

  const { name, description } = getExtraTranslated(extra);

  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const descRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = descRef.current;
    if (el) {
      setIsClamped(el.scrollHeight > el.clientHeight);
    }
  }, [description]);

  const isSelected = isMandatory || currValue > 0;
  const Icon = getIconByKeyword(name);

  const handleCheckboxToggle = () => {
    updateCartItemOptions({ ...extra, value: isSelected ? 0 : 1 }, cartItem.id);
  };

  const renderControl = () => {
    if (isMandatory) {
      return (
        <span className="c-extra-card__badge c-extra-card__badge--disabled">
          <Check size={16} strokeWidth={2.5} />
        </span>
      );
    }

    if (isCheckbox) {
      return (
        <span className={`c-extra-card__checkbox ${isSelected ? "c-extra-card__checkbox--checked" : ""}`}>
          {isSelected && <Check size={14} strokeWidth={2.5} />}
        </span>
      );
    }

    return (
      <NumInput
        value={displayValue}
        onChange={(value) => updateCartItemOptions({ ...extra, value }, cartItem.id)}
        decrementDisabled={displayValue <= 0}
        incrementDisabled={displayValue >= 5}
        max={5}
      />
    );
  };

  const isClickable = isCheckbox && !isMandatory;

  return (
    <div
      className={`c-extra-card ${isSelected ? "c-extra-card--selected" : ""}${
        isClickable ? " c-extra-card--clickable" : ""
      }`}
      onClick={isClickable ? handleCheckboxToggle : undefined}
    >
      <div className="c-extra-card__left">
        <div className={`c-extra-card__icon ${isSelected ? "c-extra-card__icon--selected" : ""}`}>
          <Icon size={18} />
        </div>
        <div className="c-extra-card__info">
          <h4 className="c-extra-card__name u-p0 u-fw-500">{name}</h4>
          {description && (
            <div className="c-extra-card__desc-wrap">
              <p
                ref={descRef}
                className={`c-extra-card__desc${isDescExpanded ? " c-extra-card__desc--expanded" : ""}`}
              >
                {description}
              </p>
              {isClamped && (
                <button
                  className="c-extra-card__desc-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDescExpanded((prev) => !prev);
                  }}
                >
                  {isDescExpanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )}
          <p className="c-extra-card__price u-p1">
            {formatCurrency(extra.price)}{" "}
            <span className="c-extra-card__rule">{translateCalculationRule(extra.calculationRule)}</span>
          </p>
        </div>
      </div>
      <div className="c-extra-card__right">{renderControl()}</div>
    </div>
  );
};
